import dayjs, {Dayjs} from 'dayjs';
import {t} from '../../../i18n';
import {ComplaintTaskRow} from '../../../graphql/complaintTask/types';

/** 后端时间为 ISO-8601 UTC；按浏览器本地时区显示（P28）。 */
export const toLocal = (iso?: string | null): Dayjs | null => (iso ? dayjs(iso) : null);

/** 今天 → HH:mm；昨天 → 昨日 HH:mm；其余 → MM-DD HH:mm。 */
export const formatTime = (iso?: string | null, now: Dayjs = dayjs()): string => {
  const d = toLocal(iso);
  if (!d) return '—';
  if (d.isSame(now, 'day')) return d.format('HH:mm');
  if (d.isSame(now.subtract(1, 'day'), 'day')) return `${t('complaintTask.time.yesterday')} ${d.format('HH:mm')}`;
  return d.format('MM-DD HH:mm');
};

/** 提报时间：始终带月日（10-17 11:11，与原型一致）；跨年时补年份（2025-12-31 23:10）。 */
export const formatDateTime = (iso?: string | null, now: Dayjs = dayjs()): string => {
  const d = toLocal(iso);
  if (!d) return '—';
  return d.format(d.isSame(now, 'year') ? 'MM-DD HH:mm' : 'YYYY-MM-DD HH:mm');
};

/** 毫秒 → 「1天23小时」/「3小时35分」/「21分钟」。 */
export const formatDuration = (ms: number): string => {
  const minutes = Math.max(0, Math.floor(ms / 60000));
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  if (days > 0) return t('complaintTask.time.daysHours', {days, hours});
  if (hours > 0) return t('complaintTask.time.hoursMinutes', {hours, minutes: mins});
  return t('complaintTask.time.minutes', {minutes: mins});
};

const ms = (a?: string | null, b?: string | null) => dayjs(a).valueOf() - dayjs(b).valueOf();

/** 剩余不超过 10 分钟算「即将超时」，倒计时变黄（10-13 确认，与 Lark 响应预警提前量同口径）。 */
export const NEAR_DUE_MS = 10 * 60 * 1000;

export interface TimelinessTag {
  kind: 'responseOverdue' | 'resolveOverdue' | 'responseLeft' | 'resolveLeft' | 'onTime';
  /** 超时多久 / 还剩多久（毫秒）；未超时标签无 */
  ms?: number;
  /** 剩余时间标签是否即将超时 */
  nearDue?: boolean;
}

const left = (kind: 'responseLeft' | 'resolveLeft', remaining: number): TimelinessTag =>
  ({kind, ms: remaining, nearDue: remaining <= NEAR_DUE_MS});

/**
 * 时效判定（设计文档 §5.2，与后端筛选口径一致）：
 * - 处理中：响应、处理各一个标签（超时 X / 剩 X），未响应时处理时限也照常倒计时（原型：两个标签同时展示）；
 * - 已解决：用关单时冻结的超时标记；
 * - 时限为空 = 不考核。
 */
export const timeliness = (row: ComplaintTaskRow, serverNow?: string | null): TimelinessTag[] => {
  const now = serverNow ?? new Date().toISOString();
  const tags: TimelinessTag[] = [];
  if (row.status === 70) {
    if (row.responseBreached && row.actualStartAt && row.responseDueAt) {
      tags.push({kind: 'responseOverdue', ms: ms(row.actualStartAt, row.responseDueAt)});
    }
    if (row.resolveBreached && row.finishAt && row.resolveDueAt) {
      tags.push({kind: 'resolveOverdue', ms: ms(row.finishAt, row.resolveDueAt)});
    }
    return tags.length ? tags : [{kind: 'onTime'}];
  }
  if (row.status !== 40) return tags;
  if (row.responseDueAt) {
    const respAt = row.actualStartAt ?? now;
    const over = ms(respAt, row.responseDueAt);
    if (over > 0) tags.push({kind: 'responseOverdue', ms: over});
    else if (!row.actualStartAt) tags.push(left('responseLeft', -over));
  }
  if (row.resolveDueAt) {
    const over = ms(now, row.resolveDueAt);
    if (over > 0) tags.push({kind: 'resolveOverdue', ms: over});
    else tags.push(left('resolveLeft', -over));
  }
  return tags;
};

export const isOverdue = (row: ComplaintTaskRow, serverNow?: string | null) =>
  timeliness(row, serverNow).some((x) => x.kind === 'responseOverdue' || x.kind === 'resolveOverdue');

export const PRIORITY_COLOR: Record<number, string> = {1: 'red', 2: 'orange', 3: 'default'};

export const priorityLabel = (p?: number | null) => (p ? t(`complaintTask.priority.${p}`) : '—');

export const roleLabel = (role?: string | null) => (role ? t(`complaintOrg.role.${role}`, role) : '');

export const subTypeLabel = (code?: string | null, name?: string | null) =>
  code ? t(`complaintTask.subType.${code}`, name ?? code) : '—';
